import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { Box, Typography, Button, Container, Paper } from "@mui/material";
import Chat from "../components/Chat";
import AuthModal from "../components/AuthModal";
import { motion, AnimatePresence } from "framer-motion";
import { useSession } from "../hooks/useSession";
import SEO from '../components/SEO';
import LazyImage from "../components/LazyImage"; // Add this import
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Fade from "@mui/material/Fade";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"; // for visual flair
import CharacterMatchupSelector from "../components/CharacterMatchupSelector"; // Add this import at the top
import PersonIcon from "@mui/icons-material/Person";

const VOTE_DURATION = 50; // seconds
const RESULT_DURATION = 10; // seconds

// Modular result card for 2 characters, always aligned and spaced
function ResultCard({ match, chars, sx = {} }) {
  if (!match || chars.length < 2) return null;
  const char1 = chars.find((c) => c.id === match.character1_id);
  const char2 = chars.find((c) => c.id === match.character2_id);
  if (!char1 || !char2) return null;

  return (
    <Paper
      sx={{
        background: "rgba(35, 35, 35, 0.85)", // Made transparent
        border: "2px solid #3e3e3e",
        borderRadius: 4,
        p: 3,
        width: 360,
        minWidth: 320,
        maxWidth: 380,
        mx: "auto",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(10px)", // Add glass effect
        WebkitBackdropFilter: "blur(10px)",
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        transition: "all 0.3s ease",
        "&:hover": {
          borderColor: "#f49330",
          boxShadow: "0 12px 40px rgba(244, 147, 48, 0.2)",
          transform: "translateY(-2px)",
        },
        ...sx,
      }}
    >
      {[char1, char2].map((char, idx) => (
        <Box
          key={char.id}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRight: idx === 0 ? "2px solid #444" : "none",
            borderLeft: idx === 1 ? "2px solid #444" : "none",
            px: 2,
            justifyContent: "flex-start",
          }}
        >
          <LazyImage
            src={char.image_url}
            alt={char.name}
            threshold={0.2}
            sx={{
              width: 90,
              height: 160,
              objectFit: "cover",
              objectPosition: "top",
              borderRadius: 3,
              mb: 2,
              border: "2px solid #444",
              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              background: "#181818",
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              mb: 2,
              textAlign: "center",
              color: "#fff",
              minHeight: 44,
              maxHeight: 44,
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1.2,
            }}
          >
            {char.name}
          </Typography>
          
          {/* Power Level Results */}
          <Box
            sx={{
              borderTop: "2px solid #444",
              width: "100%",
              textAlign: "center",
              py: 1,
              mt: 1,
              background: match.votes_power_level_0 !== undefined && 
                ((idx === 0 && match.votes_power_level_0 > match.votes_power_level_1) ||
                 (idx === 1 && match.votes_power_level_1 > match.votes_power_level_0))
                ? "rgba(244, 147, 48, 0.3)" : "rgba(244, 147, 48, 0.1)",
              border: match.votes_power_level_0 !== undefined && 
                ((idx === 0 && match.votes_power_level_0 > match.votes_power_level_1) ||
                 (idx === 1 && match.votes_power_level_1 > match.votes_power_level_0))
                ? "2px solid #f49330" : "1px solid rgba(244, 147, 48, 0.3)",
              borderRadius: 2,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 12,
                color: "#f49330",
                letterSpacing: 1,
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              Power {match.votes_power_level_0 !== undefined && 
                ((idx === 0 && match.votes_power_level_0 > match.votes_power_level_1) ||
                 (idx === 1 && match.votes_power_level_1 > match.votes_power_level_0)) && "👑"}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: "#fff",
              }}
            >
              {match.votes_power_level_0 !== undefined
                ? idx === 0
                  ? match.votes_power_level_0
                  : match.votes_power_level_1
                : ""}
            </Typography>
          </Box>
          
          {/* Popularity Results */}
          <Box
            sx={{
              borderTop: "2px solid #444",
              width: "100%",
              textAlign: "center",
              py: 1,
              background: match.votes_popularity_0 !== undefined && 
                ((idx === 0 && match.votes_popularity_0 > match.votes_popularity_1) ||
                 (idx === 1 && match.votes_popularity_1 > match.votes_popularity_0))
                ? "rgba(99, 102, 241, 0.3)" : "rgba(99, 102,241, 0.1)",
              border: match.votes_popularity_0 !== undefined && 
                ((idx === 0 && match.votes_popularity_0 > match.votes_popularity_1) ||
                 (idx === 1 && match.votes_popularity_1 > match.votes_popularity_0))
                ? "2px solid #6366f1" : "1px solid rgba(99, 102, 241, 0.3)",
              borderRadius: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 12,
                color: "#6366f1",
                letterSpacing: 1,
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              Popularity {match.votes_popularity_0 !== undefined && 
                ((idx === 0 && match.votes_popularity_0 > match.votes_popularity_1) ||
                 (idx === 1 && match.votes_popularity_1 > match.votes_popularity_0)) && "👑"}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: "#fff",
              }}
            >
              {match.votes_popularity_0 !== undefined
                ? idx === 0
                  ? match.votes_popularity_0
                  : match.votes_popularity_1
                : ""}
            </Typography>
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

export default function VersusLive() {
  const { session } = useSession();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [matchup, setMatchup] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPower, setSelectedPower] = useState(null);
  const [selectedPopularity, setSelectedPopularity] = useState(null);
  const [phase, setPhase] = useState("result");
  const [timer, setTimer] = useState(RESULT_DURATION);
  const [forceReload, setForceReload] = useState(0);
  const timerRef = useRef();
  const smoothTimerRef = useRef();
  const [smoothTimer, setSmoothTimer] = useState(RESULT_DURATION);

  // For last 3 results
  const [lastResults, setLastResults] = useState([]);
  const [lastResultsChars, setLastResultsChars] = useState([]);

  // Add state for created_by
  const [createdBy, setCreatedBy] = useState(null);

  const theme = useTheme();
  const isMobilePortrait = useMediaQuery("(max-width:600px) and (orientation: portrait)");

  // Add these state variables for the chat button:
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showMatchupSelector, setShowMatchupSelector] = useState(false);

  // Fetch the open and last 3 closed matchups
  useEffect(() => {
    async function fetchMatchup() {
      setLoading(true);

      const { data: openMatchArr } = await supabase
        .from("versus_live")
        .select("*")
        .eq("status", "open")
        .order("start_time", { ascending: false })
        .limit(1);

      const { data: closedMatchArr } = await supabase
        .from("versus_live")
        .select("*")
        .eq("status", "closed")
        .order("start_time", { ascending: false })
        .limit(3);

      const openMatch = openMatchArr && openMatchArr[0];
      setCreatedBy(openMatch?.created_by || null); // <-- set createdBy

      // For last 3 results display (always fetch latest closed)
      setLastResults(closedMatchArr);

      // Fetch all unique character IDs from last 3 matches
      const charIds = [
        ...new Set(
          closedMatchArr
            .map((m) => [m.character1_id, m.character2_id])
            .flat()
            .filter(Boolean)
        ),
      ];
      if (charIds.length > 0) {
        const { data: chars } = await supabase
          .from("characters")
          .select("*, character_tag_scores(id, tag, score, wins, losses, type)")
          .in("id", charIds);
        setLastResultsChars(chars || []);
      } else {
        setLastResultsChars([]);
      }

      if (!openMatch) {
        setMatchup(null);
        setCharacters([]);
        setLoading(false);
        return;
      }

      const startTime = new Date(openMatch.start_time).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);

      if (elapsed < RESULT_DURATION) {
        setPhase("result");
        setTimer(RESULT_DURATION - elapsed);
        setSmoothTimer(RESULT_DURATION - elapsed);
        // Show previous match for results
        const prevMatch = closedMatchArr[0] || null;
        setMatchup(prevMatch);
        if (prevMatch) {
          const { data: chars } = await supabase
            .from("characters")
            .select("*, character_tag_scores(id, tag, score, wins, losses, type)")
            .in("id", [prevMatch.character1_id, prevMatch.character2_id]);
          setCharacters(chars || []);
        } else {
          setCharacters([]);
        }
      } else if (elapsed < RESULT_DURATION + VOTE_DURATION) {
        setPhase("vote");
        setTimer(RESULT_DURATION + VOTE_DURATION - elapsed);
        setSmoothTimer(RESULT_DURATION + VOTE_DURATION - elapsed);
        setMatchup(openMatch);
        const { data: chars } = await supabase
          .from("characters")
          .select("*, character_tag_scores(id, tag, score, wins, losses, type)")
          .in("id", [openMatch.character1_id, openMatch.character2_id]);
        setCharacters(chars || []);
      } else {
        setPhase("vote");
        setTimer(VOTE_DURATION);
        setSmoothTimer(VOTE_DURATION);
        setMatchup(openMatch);
        const { data: chars } = await supabase
          .from("characters")
          .select("*, character_tag_scores(id, tag, score, wins, losses, type)")
          .in("id", [openMatch.character1_id, openMatch.character2_id]);
        setCharacters(chars || []);
      }
      setLoading(false);
    }
    fetchMatchup();
  }, [forceReload]);

  useEffect(() => {
    setSelectedPower(null);
    setSelectedPopularity(null);
  }, [matchup?.id]);

  // Smooth timer effect (updates every 50ms)
  useEffect(() => {
    if (loading) return;
    clearInterval(timerRef.current);
    clearInterval(smoothTimerRef.current);

    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          clearInterval(smoothTimerRef.current);
          setTimeout(() => {
            setForceReload((v) => v + 1);
          }, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    smoothTimerRef.current = setInterval(() => {
      setSmoothTimer((prev) => {
        if (prev <= 0.05) {
          return 0;
        }
        return prev - 0.05;
      });
    }, 50);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(smoothTimerRef.current);
    };
  }, [phase, loading, timer]);

  // Results (fetch from matchup row)
  const [results, setResults] = useState(null);
  useEffect(() => {
    if (phase === "result" && matchup) {
      setResults({
        power_level: [matchup.votes_power_level_0, matchup.votes_power_level_1],
        popularity: [matchup.votes_popularity_0, matchup.votes_popularity_1],
      });
    } else {
      setResults(null);
    }
  }, [phase, matchup]);

  // --- Update ELO, wins, and losses for characters after result phase ---
  useEffect(() => {
    if (
      phase === "result" &&
      matchup &&
      results &&
      characters.length === 2 &&
      (results.power_level[0] !== results.power_level[1] ||
        results.popularity[0] !== results.popularity[1])
    ) {
      
      // NEW: Master selection wrapper
      const executeWithMasterSelection = async () => {
        try {
          // Get all users who voted in this match
          const { data: voters } = await supabase
            .from('versus_live_votes')
            .select('user_id')
            .eq('versus_live_id', matchup.id)
            .not('user_id', 'is', null);

          const { data: { user } } = await supabase.auth.getUser();
          if (!user || !voters?.length) return;

          // Determine master (lowest user ID)
          const voterIds = voters.map(v => v.user_id).sort();
          const masterId = voterIds[0];
          
          // Only master executes ELO calculation
          if (user.id === masterId) {
            // Check if user voted on anything and increment versus_live_votes
            async function incrementUserVotes() {
              // Check if user has voted on at least one category
              const userHasVoted = (selectedPower !== null || selectedPopularity !== null);
              
              if (!userHasVoted) {
                return;
              }

              // Get current user
              const { data: { user } } = await supabase.auth.getUser();
              if (!user) {
                return;
              }

              // First get the current value
              const { data: profile, error: fetchError } = await supabase
                .from('profiles')
                .select('versus_live_votes')
                .eq('id', user.id)
                .single();
              
              if (!fetchError && profile) {
                // Then update with incremented value
                const { error } = await supabase
                  .from('profiles')
                  .update({ 
                    versus_live_votes: (profile.versus_live_votes || 0) + 1 
                  })
                  .eq('id', user.id);
                  
                if (error) {
                  console.error('Error updating versus_live_votes:', error);
                }
              }
            }
            
            function calculateElo(winnerElo, loserElo, k = 32) {
              const expectedWin = 1 / (1 + Math.pow(10, (loserElo - winnerElo) / 400));
              const expectedLose = 1 / (1 + Math.pow(10, (winnerElo - loserElo) / 400));
              const newWinnerElo = Math.round(winnerElo + k * (1 - expectedWin));
              const newLoserElo = Math.round(loserElo + k * (0 - expectedLose));
              return [newWinnerElo, newLoserElo];
            }

            async function updateTagScores(type, winnerIdx, loserIdx) {
              const winner = characters[winnerIdx];
              const loser = characters[loserIdx];

              const winnerScores = (winner.character_tag_scores || []).filter(s => s.type === type);
              const loserScores = (loser.character_tag_scores || []).filter(s => s.type === type);

              const sharedTags = winnerScores
                .map(s => s.tag)
                .filter(tag => loserScores.some(ls => ls.tag === tag));

              for (const tag of sharedTags) {
                const winnerTagScore = winnerScores.find((s) => s.tag === tag);
                const loserTagScore = loserScores.find((s) => s.tag === tag);

                if (winnerTagScore && loserTagScore) {
                  const [newWinnerElo, newLoserElo] = calculateElo(
                    winnerTagScore.score,
                    loserTagScore.score
                  );

                  await supabase
                    .from("character_tag_scores")
                    .update({
                      score: newWinnerElo,
                      wins: (winnerTagScore.wins || 0) + 1,
                    })
                    .eq("id", winnerTagScore.id);

                  await supabase
                    .from("character_tag_scores")
                    .update({
                      score: newLoserElo,
                      losses: (loserTagScore.losses || 0) + 1,
                    })
                    .eq("id", loserTagScore.id);
                }
              }
            }

            // Execute updates
            if (results.power_level[0] !== results.power_level[1]) {
              const winnerIdx = results.power_level[0] > results.power_level[1] ? 0 : 1;
              const loserIdx = 1 - winnerIdx;
              updateTagScores("power_level", winnerIdx, loserIdx);
            }
            if (results.popularity[0] !== results.popularity[1]) {
              const winnerIdx = results.popularity[0] > results.popularity[1] ? 0 : 1;
              const loserIdx = 1 - winnerIdx;
              updateTagScores("popularity", winnerIdx, loserIdx);
            }
            
            // Increment user vote count
            incrementUserVotes();
          }
        } catch (error) {
          console.error('Master selection error:', error);
        }
      };

      executeWithMasterSelection();
    }
    // eslint-disable-next-line
  }, [phase, results, characters, matchup]);

  // Insert or update vote in versus_live_votes table on selection
  const upsertVote = async (powerIdx, popularityIdx) => {
    if (!matchup || phase !== "vote") return;
    let user_id = null;
    if (supabase.auth && supabase.auth.getUser) {
      const { data } = await supabase.auth.getUser();
      user_id = data?.user?.id || null;
    }

    const { error } = await supabase.from("versus_live_votes").upsert(
      {
        versus_live_id: matchup.id,
        user_id: user_id,
        power_level_winner: powerIdx,
        popularity_winner: popularityIdx,
      },
      { onConflict: "versus_live_id,user_id" }
    );
    if (error) {
      console.error("Vote upsert error:", error.message, error);
    } else {
      console.log("Vote upserted successfully");
    }
  };

  const handlePowerSelect = (idx) => {
    const newPower = idx === selectedPower ? null : idx;
    setSelectedPower(newPower);
    upsertVote(newPower, selectedPopularity);
  };

  const handlePopularitySelect = (idx) => {
    const newPopularity = idx === selectedPopularity ? null : idx;
    setSelectedPopularity(newPopularity);
    upsertVote(selectedPower, newPopularity);
  };

  const TIMER_BAR_HEIGHT = 480;

  // Only fade when not loading and there are enough characters
  const matchupKey =
    !loading && characters.length >= 2
      ? characters.map(c => c?.id).join("-")
      : "loading";

  // Add this effect to listen for new matchups and update the queue position
  useEffect(() => {
    // Listen for new matchups in versus_live table
    const subscription = supabase
      .channel('queue_position_live')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'versus_live' },
        () => {
          // Decrement window.queuePosition if it's > 0
          if (window.queuePosition && window.queuePosition > 0) {
            window.queuePosition = window.queuePosition - 1;
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  return (
    <>
      <SEO 
        title="Live Anime Character Battles & Real-Time Voting"
        description="Join live anime character battles with real-time community voting. Watch power scaling debates unfold as the community decides who's stronger!"
        keywords="live anime battles, real-time character voting, anime powerscaling live, community anime voting, live character battles"
        url="/versus-live"
      />
      <Box sx={{ minHeight: "100vh" }}> {/* Removed background property */}
        {/* Spacer for navbar */}
        <Box sx={{ height: { xs: 72, sm: 80 } }} />
        
        {/* Login Prompt Popup - Only show during vote phase and if not logged in */}
        <AnimatePresence>
          {phase === "vote" && !session && (
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{
                position: "fixed",
                bottom: isMobilePortrait ? 16 : "50%",
                right: isMobilePortrait ? 16 : 24,
                top: isMobilePortrait ? "auto" : "50%",
                transform: isMobilePortrait ? "none" : "translateY(-50%)",
                zIndex: 1300,
              }}
            >
              <Paper
                onClick={() => setAuthModalOpen(true)}
                sx={{
                  background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                  color: "#181818",
                  p: isMobilePortrait ? 1.5 : 3,
                  borderRadius: 4,
                  cursor: "pointer",
                  boxShadow: "0 8px 32px rgba(244, 147, 48, 0.4)",
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(5px)",
                  WebkitBackdropFilter: "blur(5px)",
                  minWidth: isMobilePortrait ? 120 : 200,
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                    transform: "translateY(-2px) scale(1.02)",
                    boxShadow: "0 12px 40px rgba(244, 147, 48, 0.6)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: isMobilePortrait ? 13 : 18,
                    mb: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  🗳️ LOGIN TO VOTE
                </Typography>
                {!isMobilePortrait && (
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: 14,
                      opacity: 0.8,
                    }}
                  >
                    Join the battle now!
                  </Typography>
                )}
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>
        
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 10 } }}>
          {/* Title */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: 40, sm: 56, md: 68 },
              fontWeight: 900,
              background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              mb: 6,
              letterSpacing: 2,
            }}
          >
            VERSUS LIVE
          </Typography>

          {/* Chat Component */}
          <Box sx={{ mb: 6 }}>
            <Chat
              chatId="14f3461c-f844-496a-8949-3361feb79dfe"
              open={open}
              setOpen={setOpen}
              unread={unread}
              setUnread={setUnread}
            />
          </Box>

          {/* Main Matchup Section */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              mb: 4,
              minHeight: `${TIMER_BAR_HEIGHT}px`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column", // <-- add this for stacking
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={matchupKey}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                style={{
                  display: "flex",
                  flexDirection: isMobilePortrait ? "column" : "row",
                  gap: 5,
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: 1000,
                }}
              >
                {!loading && characters.length >= 2 && characters.map((char, idx) =>
                  isMobilePortrait ? (
                    // --- MOBILE PORTRAIT LAYOUT: 1 card per row ---
                    <Paper
                      key={char.id}
                      sx={{
                        background: "rgba(35, 35, 35, 0.85)",
                        borderRadius: 4,
                        p: 0,
                        width: "100%",
                        maxWidth: 380,
                        minHeight: 140,
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        border: "2px solid #3e3e3e",
                        color: "#fff",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "stretch",
                        overflow: "hidden",
                        mb: 2,
                        mx: "auto",
                      }}
                    >
                      {/* Image on the left, 9:16 aspect */}
                      <Box
                        sx={{
                          width: 90,
                          minWidth: 90,
                          aspectRatio: "9/16",
                          height: "auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#181818",
                          flexShrink: 0,
                          overflow: "hidden",
                        }}
                      >
                        <LazyImage
                          src={char.image_url}
                          alt={char.name}
                          threshold={0.2}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 0,
                            display: "block",
                          }}
                        />
                      </Box>
                      {/* Text and voting/results on the right */}
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          px: 2,
                          py: 2,
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#fff",
                            fontSize: 16,
                            width: "100%",
                            whiteSpace: "normal",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "left",
                            mb: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {char.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#f49330",
                            fontSize: 13,
                            fontWeight: 600,
                            width: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "left",
                            mb: 1,
                          }}
                        >
                          {char.manga}
                        </Typography>
                        {/* Voting or Results Content */}
                        <AnimatePresence mode="wait" initial={false}>
                          {phase === "vote" ? (
                            <motion.div
                              key="vote"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -20 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}
                            >
                              <Button
                                variant="contained"
                                onClick={session ? () => handlePowerSelect(idx) : () => setAuthModalOpen(true)}
                                disabled={phase !== "vote"}
                                sx={{
                                  width: "100%",
                                  py: 2,
                                  fontSize: 16,
                                  fontWeight: 600,
                                  background: selectedPower === idx 
                                    ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)"
                                    : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                                  color: selectedPower === idx ? "#181818" : "#fff",
                                  border: selectedPower === idx ? "none" : "2px solid #555",
                                  borderRadius: 3,
                                  "&:hover": {
                                    background: selectedPower === idx 
                                      ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)"
                                      : "rgba(244, 147, 48, 0.1)",
                                    borderColor: "#f49330",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                              >
                                {session 
                                  ? (selectedPower === idx ? "✓ Selected as Stronger" : "Vote Stronger")
                                  : "🔒 Login to Vote"
                                }
                              </Button>
                              
                              <Button
                                variant="contained"
                                onClick={session ? () => handlePopularitySelect(idx) : () => setAuthModalOpen(true)}
                                disabled={phase !== "vote"}
                                sx={{
                                  width: "100%",
                                  py: 2,
                                  fontSize: 16,
                                  fontWeight: 600,
                                  background: selectedPopularity === idx 
                                    ? "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)"
                                    : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                                  color: selectedPopularity === idx ? "#fff" : "#fff",
                                  border: selectedPopularity === idx ? "none" : "2px solid #555",
                                  borderRadius: 3,
                                  "&:hover": {
                                    background: selectedPopularity === idx 
                                      ? "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)"
                                      : "rgba(99, 102,241, 0.1)",
                                    borderColor: "#6366f1",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                              >
                                {session 
                                  ? (selectedPopularity === idx ? "✓ Selected as Favorite" : "Vote Favorite")
                                  : "🔒 Login to Vote"
                                }
                              </Button>
                            </motion.div>
                          ) : (
                            results && (
                              <motion.div
                                key="results"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}
                              >
                                <Box
                                  sx={{
                                    background: results.power_level[idx] > results.power_level[1 - idx] 
                                      ? "rgba(244, 147, 48, 0.3)" : "rgba(244, 147, 48, 0.1)",
                                    borderRadius: 3,
                                    p: 0,
                                    border: results.power_level[idx] > results.power_level[1 - idx]
                                      ? "2px solid #f49330" : "1px solid rgba(244, 147, 48, 0.3)",
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography sx={{ 
                                    fontWeight: 600, 
                                    color: "#f49330", 
                                    mb: 1,
                                    fontSize: 16 
                                  }}>
                                    Power Level Votes {results.power_level[idx] > results.power_level[1 - idx] && "👑"}
                                  </Typography>
                                  <Typography sx={{ fontWeight: 700, fontSize: 24, color: "#fff" }}>
                                    {results.power_level[idx]}
                                  </Typography>
                                </Box>
                                
                                <Box
                                  sx={{
                                    background: results.popularity[idx] > results.popularity[1 - idx] 
                                      ? "rgba(99, 102,241, 0.3)" : "rgba(99, 102,241, 0.1)",
                                    borderRadius: 3,
                                    p: 0,
                                    border: results.popularity[idx] > results.popularity[1 - idx]
                                      ? "2px solid #6366f1" : "1px solid rgba(99, 102, 241, 0.3)",
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography sx={{ 
                                    fontWeight: 600, 
                                    color: "#6366f1", 
                                    mb: 1,
                                    fontSize: 16 
                                  }}>
                                    Popularity Votes {results.popularity[idx] > results.popularity[1 - idx] && "👑"}
                                  </Typography>
                                  <Typography sx={{ fontWeight: 700, fontSize: 24, color: "#fff" }}>
                                    {results.popularity[idx]}
                                  </Typography>
                                </Box>
                              </motion.div>
                            )
                          )}
                        </AnimatePresence>
                      </Box>
                    </Paper>
                  ) : (
                    // --- ORIGINAL DESKTOP/TABLET LAYOUT ---
                    <Paper
                      key={char.id}
                      sx={{
                        background: "rgba(35, 35, 35, 0.85)",
                        borderRadius: 4,
                        p: 4,
                        width: 380,
                        minHeight: 480,
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        backdropFilter: "blur(10px)", // Add glass effect
                        WebkitBackdropFilter: "blur(10px)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        border: "2px solid #3e3e3e",
                        color: "#fff",
                        position: "relative",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "#f49330",
                          boxShadow: "0 12px 40px rgba(244, 147, 48, 0.2)",
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      <LazyImage
                        src={char.image_url}
                        alt={char.name}
                        threshold={0.2}
                        sx={{
                          width: 200,
                          height: 320,
                          objectFit: "cover",
                          borderRadius: 3,
                          mb: 3,
                          border: "2px solid #444",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                        }}
                      />
                      
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: "center" }}>
                        {char.name}
                      </Typography>
                      
                      <Typography sx={{ color: "#f49330", mb: 3, fontSize: 16, fontWeight: 600 }}>
                        {char.manga}
                      </Typography>

                      {/* Voting or Results Content */}
                      <AnimatePresence mode="wait" initial={false}>
                        {phase === "vote" ? (
                          <motion.div
                            key="vote"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}
                          >
                            <Button
                              variant="contained"
                              onClick={session ? () => handlePowerSelect(idx) : () => setAuthModalOpen(true)}
                              disabled={phase !== "vote"}
                              sx={{
                                width: "100%",
                                py: 2,
                                fontSize: 16,
                                fontWeight: 600,
                                background: selectedPower === idx 
                                  ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)"
                                  : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                                color: selectedPower === idx ? "#181818" : "#fff",
                                border: selectedPower === idx ? "none" : "2px solid #555",
                                borderRadius: 3,
                                "&:hover": {
                                  background: selectedPower === idx 
                                    ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)"
                                    : "rgba(244, 147, 48, 0.1)",
                                  borderColor: "#f49330",
                                },
                                transition: "all 0.3s ease",
                              }}
                            >
                              {session 
                                ? (selectedPower === idx ? "✓ Selected as Stronger" : "Vote Stronger")
                                : "🔒 Login to Vote"
                              }
                            </Button>
                            
                            <Button
                              variant="contained"
                              onClick={session ? () => handlePopularitySelect(idx) : () => setAuthModalOpen(true)}
                              disabled={phase !== "vote"}
                              sx={{
                                width: "100%",
                                py: 2,
                                fontSize: 16,
                                fontWeight: 600,
                                background: selectedPopularity === idx 
                                  ? "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)"
                                  : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                                color: selectedPopularity === idx ? "#fff" : "#fff",
                                border: selectedPopularity === idx ? "none" : "2px solid #555",
                                borderRadius: 3,
                                "&:hover": {
                                  background: selectedPopularity === idx 
                                    ? "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)"
                                    : "rgba(99, 102,241, 0.1)",
                                  borderColor: "#6366f1",
                                },
                                transition: "all 0.3s ease",
                              }}
                            >
                              {session 
                                ? (selectedPopularity === idx ? "✓ Selected as Favorite" : "Vote Favorite")
                                : "🔒 Login to Vote"
                              }
                            </Button>
                          </motion.div>
                        ) : (
                          results && (
                            <motion.div
                              key="results"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -20 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}
                            >
                              <Box
                                sx={{
                                  background: results.power_level[idx] > results.power_level[1 - idx] 
                                    ? "rgba(244, 147, 48, 0.3)" : "rgba(244, 147, 48, 0.1)",
                                  borderRadius: 3,
                                  p: 0,
                                  border: results.power_level[idx] > results.power_level[1 - idx]
                                    ? "2px solid #f49330" : "1px solid rgba(244, 147, 48, 0.3)",
                                  textAlign: "center",
                                }}
                              >
                                <Typography sx={{ 
                                  fontWeight: 600, 
                                  color: "#f49330", 
                                  mb: 1,
                                  fontSize: 16 
                                }}>
                                  Power Level Votes {results.power_level[idx] > results.power_level[1 - idx] && "👑"}
                                </Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: 24, color: "#fff" }}>
                                  {results.power_level[idx]}
                                </Typography>
                              </Box>
                              
                              <Box
                                sx={{
                                  background: results.popularity[idx] > results.popularity[1 - idx] 
                                    ? "rgba(99, 102,241, 0.3)" : "rgba(99, 102,241, 0.1)",
                                  borderRadius: 3,
                                  p: 0,
                                  border: results.popularity[idx] > results.popularity[1 - idx]
                                    ? "2px solid #6366f1" : "1px solid rgba(99, 102, 241, 0.3)",
                                  textAlign: "center",
                                }}
                              >
                                <Typography sx={{ 
                                  fontWeight: 600, 
                                  color: "#6366f1", 
                                  mb: 1,
                                  fontSize: 16 
                                }}>
                                  Popularity Votes {results.popularity[idx] > results.popularity[1 - idx] && "👑"}
                                </Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: 24, color: "#fff" }}>
                                  {results.popularity[idx]}
                                </Typography>
                              </Box>
                            </motion.div>
                          )
                        )}
                      </AnimatePresence>
                    </Paper>
                  )
                )}
              </motion.div>
            </AnimatePresence>

            {/* TIMER BAR for MOBILE PORTRAIT: now under cards, above phase indicator */}
            {isMobilePortrait && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  mb: -2,
                  mt: 0,
                }}
              >
                <Box
                  sx={{
                    width: 300,
                    height: 20,
                    background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                    borderRadius: 3,
                    overflow: "hidden",
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    border: "2px solid #444",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      background: phase === "vote"
                        ? "linear-gradient(90deg, #f49330 0%, #fff176 100%)"
                        : "linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)",
                      transition: "width 0.1s linear",
                      width: `${
                        phase === "vote"
                          ? (smoothTimer / VOTE_DURATION) * 100
                          : (smoothTimer / RESULT_DURATION) * 100
                      }%`,
                      borderRadius: "3px",
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* TIMER BAR for DESKTOP/TABLET: keep original absolute position */}
            {!isMobilePortrait && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: { xs: 5, md: 200 },
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  height: TIMER_BAR_HEIGHT,
                  minWidth: 80,
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: TIMER_BAR_HEIGHT,
                    background: "linear-gradient(135deg, #1a1a1a 0%, #2e2e2e 100%)",
                    borderRadius: 3,
                    overflow: "hidden",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    border: "2px solid #444",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      bottom: 0,
                      width: "100%",
                      background: phase === "vote"
                        ? "linear-gradient(180deg, #f49330 0%, #fff176 100%)"
                        : "linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)",
                      transition: "height 0.1s linear",
                      height: `${
                        phase === "vote"
                          ? (smoothTimer / VOTE_DURATION) * 100
                          : (smoothTimer / RESULT_DURATION) * 100
                      }%`,
                      borderRadius: "0 0 8px 8px",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Phase Indicator - Moved under character cards */}
          <Box sx={{ textAlign: "center", mb: 8 }}>
            {/* Requested by username or Random */}
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 600,
                fontSize: { xs: 16, sm: 18 },
                mb: 1,
                letterSpacing: 1,
              }}
            >
              Requested by: {createdBy ? createdBy : "Random"}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                background: phase === "vote" 
                  ? "rgba(244, 147, 48, 0.1)" 
                  : "rgba(99, 102, 241, 0.1)",
                border: phase === "vote" 
                  ? "2px solid rgba(244, 147, 48, 0.3)" 
                  : "2px solid rgba(99, 102, 241, 0.3)",
                borderRadius: 4,
                px: 4,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  color: phase === "vote" ? "#f49330" : "#6366f1",
                  fontWeight: 700,
                  fontSize: { xs: 18, sm: 24 },
                }}
              >
                {phase === "vote" ? "🗳️ VOTING PHASE" : "📊 RESULTS"}
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: 16, sm: 20 },
                  background: phase === "vote" ? "#f49330" : "#6366f1",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                {Math.ceil(smoothTimer)}s
              </Typography>
            </Box>
          </Box>

          {/* Last Battles Section */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#fff",
                mb: 6,
                fontSize: { xs: 28, sm: 36 },
              }}
            >
              Recent Battles
            </Typography>
            
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: 4,
                flexWrap: { xs: "wrap", lg: "nowrap" },
                maxWidth: 1200,
                mx: "auto",
              }}
            >
              {lastResults.slice(0, 3).map((match, i) => (
                <ResultCard
                  key={match.id}
                  match={match}
                  chars={lastResultsChars.filter(
                    (c) => c.id === match.character1_id || c.id === match.character2_id
                  )}
                />
              ))}
            </Box>
          </Box>
        </Container>
        
        <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
        
        {/* Chat button - fixed position, always visible */}
        <Box
          sx={{
            position: "fixed",
            left: isMobilePortrait ? 12 : 24, // small gap from left on mobile
            bottom: isMobilePortrait ? 12 : 32, // small gap from bottom on mobile
            zIndex: 1300,
          }}
        >
          <Fade in={!open}>
            <Badge
              badgeContent={unread > 9 ? "9+" : unread}
              color="warning"
              invisible={unread === 0}
              sx={{
                "& .MuiBadge-badge": {
                  fontWeight: 700,
                  fontSize: 18,
                  minWidth: 28,
                  height: 28,
                  right: -8,
                  top: 8,
                  background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                  color: "#181818",
                  border: "2px solid #181818",
                  boxShadow: "0 2px 8px rgba(244, 147, 48, 0.4)",
                },
              }}
            >
              <IconButton
                color="primary"
                sx={{
                  background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
                  border: "2px solid #f49330",
                  width: isMobilePortrait ? 63 : 84,   // 0.75x size for mobile
                  height: isMobilePortrait ? 63 : 84,  // 0.75x size for mobile
                  "&:hover": { 
                    background: "linear-gradient(135deg, #2e2e2e 0%, #3a3a3a 100%)",
                    borderColor: "#fff176",
                    boxShadow: "0 8px 24px rgba(244, 147, 48, 0.3)",
                    transform: "translateY(-2px)",
                  },
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                  transition: "all 0.3s ease",
                }}
                onClick={() => {
                  setOpen(true);
                  setUnread(0);
                }}
                size="large"
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: isMobilePortrait ? 36 : 48, color: "#f49330" }} />
              </IconButton>
            </Badge>
          </Fade>
        </Box>

        {/* Request Matchup button - fixed position, vertically centered, above chat button */}
        <Box
          sx={{
            position: "fixed",
            right: isMobilePortrait ? 12 : 24,
            bottom: isMobilePortrait ? 12 : "auto",
            top: isMobilePortrait ? "auto" : "50%",
            transform: isMobilePortrait ? "none" : "translateY(-50%)",
            zIndex: 1300,
            width: isMobilePortrait ? 56 : "auto",
            height: isMobilePortrait ? 56 : "auto",
            p: 0,
          }}
        >
          <Fade in={!showMatchupSelector}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                minWidth: isMobilePortrait ? 56 : undefined,
                width: isMobilePortrait ? 56 : undefined,
                height: isMobilePortrait ? 56 : undefined,
                borderRadius: isMobilePortrait ? "50%" : 4,
                background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                color: "#181818",
                fontWeight: 700,
                fontSize: { xs: 16, md: 20 },
                boxShadow: "0 8px 32px rgba(244, 147, 48, 0.3)",
                border: "2px solid #f49330",
                transition: "all 0.3s",
                textAlign: "center",
                justifyContent: "center",
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                  borderColor: "#fff176",
                  transform: "scale(1.05)",
                  boxShadow: "0 12px 40px rgba(244, 147, 48, 0.4)",
                },
              }}
              onClick={() => setShowMatchupSelector(true)}
            >
              {isMobilePortrait ? (
                <PersonIcon sx={{ fontSize: 32, color: "#181818" }} />
              ) : (
                (window.queuePosition && window.queuePosition > 0)
                  ? `Position : ${window.queuePosition}`
                  : "REQUEST MATCHUP"
              )}
            </Button>
          </Fade>
        </Box>

        {/* CharacterMatchupSelector modal */}
        {showMatchupSelector && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 1399,
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowMatchupSelector(false)}
          >
            <Box
              sx={{ position: "relative", zIndex: 1400 }}
              onClick={e => e.stopPropagation()}
            >
              <CharacterMatchupSelector onClose={() => setShowMatchupSelector(false)} />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}